import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import {
  DatePicker,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  UserNav,
  BusinessSummary,
  BusinessTable,
  Select,
} from '../../components';
import { TopbarContainer } from '../../containers';
import config from '../../config';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import css from './BusinessForecastPage.css';

const toIsoLocal = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = new Date(); // local time

// Yesterday
const yesterday = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() - 1
);

// Same day last year
const sameDayLastYear = new Date(
  yesterday.getFullYear() - 1,
  yesterday.getMonth(),
  yesterday.getDate()
);

const currentYear = today.getFullYear();
const currentMonth = today.getMonth();

// First day of month
const firstDay = new Date(currentYear, currentMonth, 1);

// Last day of time period - today
const lastDay = today;

// Last dy of month
const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

// ISO format
const sameDayLastYearISO = toIsoLocal(sameDayLastYear);
const yesterdayISO = toIsoLocal(yesterday);
const firstDayISO = toIsoLocal(firstDay);
const lastDayISO = toIsoLocal(lastDay);
const lastDayOfMonthISO = toIsoLocal(lastDayOfMonth);

const countryData = [
  { value: 'Polska (PLN)', countryId: 'pl', currency: 'PLN' },
  { value: 'Magyarország (HUF)', countryId: 'hu', currency: 'HUF' },
  { value: 'Česká republika (CZK)', countryId: 'cz', currency: 'CZK' },
  { value: 'Slovensko (EUR)', countryId: 'sk', currency: 'EUR' }
];

const FetchData = ({date,  onLoadData, intl, start, end, prev_start, prev_end, currency, countryId, isDecathlon }) => {
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isComparisonOn = prev_start && prev_end;

  useEffect(() => {
    setLoading(true)
    Promise.all([
      onLoadData({ createdAtLessThan: yesterdayISO, endGreaterThan: start, endLessThan: end, currency, countryId, isDecathlon, businessCategory: '*' }),
      prev_start && prev_end ? onLoadData({ createdAtLessThan: sameDayLastYearISO, endGreaterThan: prev_start, endLessThan: prev_end, currency, countryId, isDecathlon, businessCategory: '*' }) : Promise.resolve({ data: null })
    ]).then((res) => {
      setData(res[0].data?.data);
      setPrevData(res[1].data?.data);
      setLoading(false)
    })
  }, [start, end, prev_start, prev_end, currency, countryId, isDecathlon]);

  const concatenatedAdditionalData = data ? data.slice(1).map(provider => {
    const providerPrevData = prevData ? prevData.find(i => i.id.uuid !== 'all' && i.attributes.businessCategory === provider.attributes.businessCategory) : null;

    return {
      label: provider.attributes.businessCategory || 'other',
      providerName: provider.attributes.businessCategory,
      data: [
        { current: provider ? (provider.attributes.incomeTotal / 100).toFixed(2) : 0, prev: providerPrevData ? (providerPrevData.attributes.incomeTotal / 100).toFixed(2) : 0 },
        { current: provider ? provider.attributes.itemsCount : 0, prev: providerPrevData ? providerPrevData.attributes.itemsCount : 0 },
        { current: provider ? ((provider.attributes.incomeTotal / provider.attributes.itemsCount) / 100).toFixed(2) : 0, prev: providerPrevData ? ((providerPrevData.attributes.incomeTotal / providerPrevData.attributes.itemsCount) / 100).toFixed(2) : 0 },
        { current: provider ? (provider.attributes.avgDuration * 1).toFixed(1) : 0, prev: providerPrevData ? (providerPrevData.attributes.avgDuration * 1).toFixed(1) : 0 }
      ],
    }
  }) : [];

  if (!data || loading) return null;

  return (
    <BusinessTable
      rows={concatenatedAdditionalData}
      isComparisonOn={isComparisonOn}
      sortBy={0}
      firstRowName={intl.formatMessage({ id: 'General.category' })}
      dataHeaders={[
        { primary: intl.formatMessage({ id: 'BusinessResults.turnover' }), secondary: `(${currency})` },
        { primary: intl.formatMessage({ id: 'BusinessResults.quantity' }), secondary: `(${intl.formatMessage({ id: 'BusinessResults.count' })})` },
        { primary: intl.formatMessage({ id: 'BusinessResults.avgRentingValue' }), secondary: `(${currency})` },
        { primary: intl.formatMessage({ id: 'BusinessResults.avgRentingLength' }), secondary: `(${intl.formatMessage({ id: 'BusinessResults.days' })})` }
      ]}
      dates={{ firstDay, lastDay }}
      extendableRows={false}
    />
  )
}

export const BusinessForecastPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    location,
    history,
    onLoadData,
    analysis,
    currentUser
  } = props;

  const countryUser = countryData.find(({ countryId }) => countryId === currentUser?.attributes?.language);
  const defaultCurrency = countryUser ? countryUser.currency : 'PLN';
  const defaultCountryId = config.custom.countryId;

  const {
    createdUntil = yesterdayISO,
    createdUntilPrev = sameDayLastYearISO,
    start = firstDayISO,
    end = lastDayOfMonthISO,
    prev_start,
    prev_end,
    currency = defaultCurrency,
    countryId = defaultCountryId,
    isDecathlon,
    date
  } = parse(location.search);
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [additionalData, setAdditionalData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {

    Promise.all([
      onLoadData({ date, createdAtLessThan: createdUntil, endGreaterThan: start, endLessThan: end, currency, countryId, isDecathlon, providerId: '*', include: ['provider'] }),
      prev_start && prev_end ? onLoadData({ date, createdAtLessThan: createdUntilPrev, endGreaterThan: prev_start, endLessThan: prev_end, currency, countryId, isDecathlon, providerId: '*', include: ['provider'] }) : Promise.resolve({ data: null })
    ]).then((res) => {
      setData(res[0].data);
      setPrevData(res[1].data);
    })
  }, [start, end, prev_start, prev_end, currency, countryId, isDecathlon])

  const title = intl.formatMessage({ id: "BusinessForecastPage.title" });

  const handleDateRangeSubmit = ({ start, end, prevStart, prevEnd }) => {
    const prevDates = prevStart && prevEnd ? { prev_start: prevStart, prev_end: prevEnd } : {};
    history.push(
      createResourceLocatorString(
        'BusinessForecastPage',
        routeConfiguration(),
        {},
        { start, end, currency, countryId, isDecathlon, ...prevDates }
      )
    );
  }

  const handleCurrencyChange = (e) => {
    const prevDates = prev_start && prev_end ? { prev_start, prev_end } : {};

    const country = countryData.find(country => country.countryId === e.target.value);
    history.push(
      createResourceLocatorString(
        'BusinessForecastPage',
        routeConfiguration(),
        {},
        { currency: country.currency.toUpperCase(), countryId: country.countryId, start, end, isDecathlon, ...prevDates }
      )
    );
  }

  const handleProviderTypesChange = (e) => {
    const prevDates = prev_start && prev_end ? { prev_start, prev_end } : {};
    history.push(
      createResourceLocatorString(
        'BusinessForecastPage',
        routeConfiguration(),
        {},
        { isDecathlon: e.target.value === 'true' || (e.target.value === 'false' ? false : undefined), currency, countryId, start, end,  ...prevDates }
      )
    );
  }

  const isComparisonOn = prev_start && prev_end;
  const basicData = data ? data.data.slice(1) : [];
  const previousData = prevData ? prevData.data.slice(1) : [];
  const basicIncluded = data ? data.included : [];
  const previousIncluded = prevData ? prevData.included : [];

  const allProvidersData = [... new Map([...basicData, ...previousData].map(item => [item.relationships.provider.data.id.uuid, item])).values()];
  const allProvidersIncluded = [...basicIncluded, ...previousIncluded];

  const concatenatedData = data ? allProvidersData.map(provider => {
    const providerId = provider.relationships.provider.data.id.uuid;
    const providerData = allProvidersIncluded.find(i => i.id.uuid === provider.relationships.provider.data.id.uuid);
    const providerNumbers = data ? data.data.find(i => i.relationships.provider.data?.id.uuid === providerId) : null;
    const prevProviderData = prevData ? prevData.data.find(i => i.relationships.provider.data?.id.uuid === providerId) : null;

    const currentProviderAvg = providerNumbers && providerNumbers.attributes.transactionsCount > 0 ? (providerNumbers.attributes.incomeTotal / providerNumbers.attributes.transactionsCount) : 0;
    const prevProviderAvg = prevProviderData && prevProviderData.attributes.transactionsCount ? (prevProviderData.attributes.incomeTotal / prevProviderData.attributes.transactionsCount) : 0;

    return {
      label: providerData.attributes.name,
      providerId: providerData.id.uuid,
      providerName: providerData.attributes.name,
      data: [
        { current: providerNumbers ? (providerNumbers.attributes.incomeTotal / 100).toFixed(2) : 0, prev: prevProviderData ? (prevProviderData.attributes.incomeTotal / 100).toFixed(2) : 0 },
        { current: providerNumbers ? providerNumbers.attributes.transactionsCount : 0, prev: prevProviderData ? prevProviderData.attributes.transactionsCount : 0 },
        { current: providerNumbers ? (currentProviderAvg / 100).toFixed(2) : 0, prev: prevProviderData ? (prevProviderAvg / 100).toFixed(2) : 0 },
        // { current: providerNumbers ? (providerNumbers.attributes.incomeMax / 100).toFixed(2) : 0, prev: prevProviderData ? (prevProviderData.attributes.incomeMax / 100).toFixed(2) : 0 },
        { current: providerNumbers ? (providerNumbers.attributes.avgDuration * 1).toFixed(1) : 0, prev: prevProviderData ? (prevProviderData.attributes.avgDuration * 1).toFixed(1) : 0 }
      ],
    }
  }) : [];

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="BusinessDktPage"
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
          <UserNav selectedPageName="BusinessDktPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="BusinessDktPage" />
        <LayoutWrapperMain className={css.colorBackground}>
          <div className={css.content}>
            <div className={css.header}>
              <h1 className={css.title}>
                {intl.formatMessage({ id: 'BusinessDktPage.yourResults' })}
              </h1>
              <div>
                <DatePicker
                  onSubmit={handleDateRangeSubmit}
                  value={{ startDate: start, endDate: end, prevStartDate: prev_start, prevEndDate: prev_end }}
                  showComparison
                  futureDatesOn
                />
              </div>
            </div>
            <div className={css.filters}>
              <Select
                label={intl.formatMessage({ id: 'BusinessDktPage.country' })}
                value={countryId}
                onChange={handleCurrencyChange}
              >
                {countryData.map(({ value, countryId }) => (<option value={countryId}>{value}</option>))}
              </Select>
              <Select
                label={intl.formatMessage({ id: 'BusinessDktPage.filteringProviderType' })}
                value={isDecathlon}
                onChange={handleProviderTypesChange}
              >
                <option value="all">{intl.formatMessage({ id: 'BusinessDktPage.filterProviderTypeAll' })}</option>
                <option value="true">{intl.formatMessage({ id: 'BusinessDktPage.filterProviderTypeDecathlon' })}</option>
                <option value="false">{intl.formatMessage({ id: 'BusinessDktPage.filterProviderTypeExternal' })}</option>
              </Select>
              <div>
                {createdUntil}{prev_start && prev_end && ` vs ${createdUntilPrev}`}
              </div>
            </div>
            {
              data &&
              <BusinessSummary
                currency={currency}
                currentData={{
                  payinTotal: data.data[0].attributes.incomeTotal,
                  transactionsCount: data.data[0].attributes.transactionsCount,
                  payoutTotal: data.data[0].attributes.payoutTotal,
                  payinAvg: data.data[0].attributes.incomeAvg,
                  payinMax: data.data[0].attributes.incomeMax,
                  avgDuration: data.data[0].attributes.avgDuration,
                  itemsCount: data.data[0].attributes.itemsCount,
                }}
                previousData={prevData && {
                  payinTotal: prevData.data[0].attributes.incomeTotal,
                  transactionsCount: prevData.data[0].attributes.transactionsCount,
                  payoutTotal: prevData.data[0].attributes.payoutTotal,
                  payinAvg: prevData.data[0].attributes.incomeAvg,
                  payinMax: prevData.data[0].attributes.incomeMax,
                  avgDuration: prevData.data[0].attributes.avgDuration,
                  itemsCount: prevData.data[0].attributes.itemsCount,
                }}
              />
            }
            {
              data &&
              <BusinessTable
                rows={concatenatedData}
                isComparisonOn={isComparisonOn}
                sortBy={0}
                dataHeaders={[
                  { primary: intl.formatMessage({ id: 'BusinessResults.turnover' }), secondary: `(${currency})` },
                  { primary: intl.formatMessage({ id: 'BusinessResults.rentings' }), secondary: `(${intl.formatMessage({ id: 'BusinessResults.count' })})` },
                  { primary: intl.formatMessage({ id: 'BusinessResults.avgRentingValue' }), secondary: `(${currency})` },
                  // { primary: 'Maks. wartość wyp.', secondary: `(${currency})` },
                  { primary: intl.formatMessage({ id: 'BusinessResults.avgRentingLength' }), secondary: `(${intl.formatMessage({ id: 'BusinessResults.days' })})` }
                ]}
                dates={{ firstDay, lastDay }}
                extendableRows={false}
              />
            }
            <FetchData
              onLoadData={onLoadData}
              date={date}
              intl={intl}
              start={start}
              end={end}
              prev_start={prev_start}
              prev_end={prev_end}
              currency={currency}
              countryId={countryId}
              isDecathlon={isDecathlon}
            />
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  // Topbar needs user info.
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const loadData = params => (dispatch, getState, sdk) => {
  return sdk.newSdk.analyticsBookings.query({ ...params, })
    .then((res) => {
      return res;
    })
    .catch(e => {
      console.error(e, 'initial-message-send-failed', { tx: 'PLN' });
    });
};

const mapDispatchToProps = dispatch => ({
  onLoadData: (params) => dispatch(loadData(params))
});

const BusinessForecastPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl,
  withRouter
)(BusinessForecastPageComponent);

export default BusinessForecastPage;
